import React, { useEffect, useRef, useState } from "react";
import Pusher from "pusher-js";

export const SocketConnectionBase = ({ attrs, showTitle = "📡 Socket Connection Monitor" }) => {
    const [messages, setMessages] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState("Desconectado");

    const pusherRef = useRef(null);
    const channelRef = useRef(null);

    // Conectar a Pusher
    useEffect(() => {
        const {
            vtPusherKey,
            vtPusherCluster,
            vtPusherChannel,
            vtPusherEvent,
            vtPusherSecret,
            vtPusherAppId,
        } = attrs;

        // Nota importante: secret y appId no se usan en el cliente
        if (vtPusherSecret || vtPusherAppId) {
            console.info(
                "Nota: vtPusherSecret y vtPusherAppId se ignoran en el frontend (solo backend)."
            );
        }

        // Validación mínima para canales públicos
        if (!vtPusherKey || !vtPusherCluster || !vtPusherChannel || !vtPusherEvent) {
            setConnectionStatus("Error: Faltan parámetros requeridos");
            return;
        }

        setConnectionStatus("Conectando...");

        // Limpiar conexiones previas
        if (channelRef.current) {
            try {
                channelRef.current.unbind_all();
                channelRef.current.unsubscribe?.();
            } catch {}
            channelRef.current = null;
        }
        if (pusherRef.current) {
            try {
                pusherRef.current.disconnect();
            } catch {}
            pusherRef.current = null;
        }

        // Crear cliente Pusher
        const pusher = new Pusher(vtPusherKey, {
            cluster: vtPusherCluster,
            forceTLS: true,
        });
        pusherRef.current = pusher;

        const channel = pusher.subscribe(vtPusherChannel);
        channelRef.current = channel;

        // Logs de suscripción
        const onSucceeded = () => {
            const successMsg = `✅ Suscrito a "${vtPusherChannel}". Escuchando evento "${vtPusherEvent}"...`;
            console.log(successMsg);
            setConnectionStatus("Conectado");
            setMessages((prev) => [
                ...prev,
                {
                    type: "success",
                    timestamp: new Date().toLocaleTimeString(),
                    message: successMsg,
                },
            ]);
        };

        const onError = (status) => {
            const errorMsg = `❌ Error de suscripción: ${JSON.stringify(status)}`;
            console.error(errorMsg);
            setConnectionStatus("Error");
            setMessages((prev) => [
                ...prev,
                {
                    type: "error",
                    timestamp: new Date().toLocaleTimeString(),
                    message: errorMsg,
                },
            ]);
        };

        const onData = (data) => {
            // Emitir evento a VTWidgetBridge si está disponible
            if (window?.VTWidgetBridge?.Bridge) {
                const manager = window.VTWidgetBridge.Bridge.raiseEvent("vtMessageRecived", {
                    vtOutChannel: vtPusherChannel,
                    vtOutEvent: vtPusherEvent,
                    vtOutData: JSON.stringify(data)
                });
                console.log("📨 Mensaje recibido:", manager);
            } else {
                console.log("📨 Mensaje recibido:");
                console.log("Canal:", vtPusherChannel);
                console.log("Evento:", vtPusherEvent);
                console.log("Datos:", data);
                console.log("---");
            }

            // Agregar a la lista de mensajes mostrados en la UI
            setMessages((prev) => [
                ...prev,
                {
                    type: "data",
                    timestamp: new Date().toLocaleTimeString(),
                    channel: vtPusherChannel,
                    event: vtPusherEvent,
                    data: data,
                },
            ]);
        };

        channel.bind("pusher:subscription_succeeded", onSucceeded);
        channel.bind("pusher:subscription_error", onError);
        channel.bind(vtPusherEvent, onData);

        // Cleanup al desmontar
        return () => {
            try {
                channel.unbind("pusher:subscription_succeeded", onSucceeded);
                channel.unbind("pusher:subscription_error", onError);
                channel.unbind(vtPusherEvent, onData);
                pusher.unsubscribe(vtPusherChannel);
                pusher.disconnect();
            } catch {}
            channelRef.current = null;
            pusherRef.current = null;
            setConnectionStatus("Desconectado");
        };
    }, [attrs]);

    // Función para limpiar mensajes
    const clearMessages = () => {
        setMessages([]);
    };

    // Obtener icono según estado de conexión
    const getConnectionIcon = () => {
        switch (connectionStatus) {
            case "Conectado":
                return "🟢";
            case "Conectando...":
                return "🟡";
            case "Error":
            case "Error: Faltan parámetros requeridos":
                return "🔴";
            case "Desconectado":
            default:
                return "⚫";
        }
    };

    return (
        <div style={{
            width: "100%",
            height: "100%",
            padding: "20px",
            backgroundColor: "#1e1e1e",
            color: "#fff",
            fontFamily: "monospace",
            overflow: "auto",
            fontSize: "14px"
        }}>
            <h1 style={{ marginBottom: "20px", color: "#ED0029", fontSize: "24px" }}>
                {showTitle}
            </h1>

            {/* Estado de conexión */}
            <div style={{
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px",
                borderLeft: `4px solid ${
                    connectionStatus === "Conectado" ? "#00ff00" :
                    connectionStatus === "Error" || connectionStatus === "Error: Faltan parámetros requeridos" ? "#ff0000" :
                    connectionStatus === "Conectando..." ? "#ffff00" :
                    "#808080"
                }`
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{ fontSize: "24px" }}>{getConnectionIcon()}</span>
                    <div>
                        <strong>Estado:</strong> {connectionStatus}
                    </div>
                </div>
            </div>

            {/* Configuración */}
            <div style={{
                marginBottom: "20px",
                padding: "15px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px"
            }}>
                <h2 style={{ marginBottom: "10px", fontSize: "18px", display: "flex", alignItems: "center", gap: "8px" }}>
                    ⚙️ Configuración
                </h2>
                <div style={{ fontSize: "14px", lineHeight: "1.8" }}>
                    <div><strong>App ID:</strong> {attrs.vtPusherAppId || "N/A"}</div>
                    <div><strong>Key:</strong> {attrs.vtPusherKey || "N/A"}</div>
                    <div><strong>Cluster:</strong> {attrs.vtPusherCluster || "N/A"}</div>
                    <div><strong>Channel:</strong> {attrs.vtPusherChannel || "N/A"}</div>
                    <div><strong>Event:</strong> {attrs.vtPusherEvent || "N/A"}</div>
                </div>
            </div>

            {/* Botón para limpiar mensajes */}
            <button
                onClick={clearMessages}
                style={{
                    marginBottom: "20px",
                    padding: "10px 20px",
                    backgroundColor: "#ED0029",
                    color: "#fff",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold"
                }}
            >
                🗑️ Limpiar Mensajes
            </button>

            {/* Lista de mensajes */}
            <div style={{
                padding: "15px",
                backgroundColor: "#2d2d2d",
                borderRadius: "5px"
            }}>
                <h2 style={{ marginBottom: "10px", fontSize: "18px", display: "flex", alignItems: "center", gap: "8px" }}>
                    📋 Actividad ({messages.length})
                </h2>
                <div style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    fontSize: "14px"
                }}>
                    {messages.length === 0 ? (
                        <div style={{ color: "#808080", fontStyle: "italic", padding: "20px", textAlign: "center" }}>
                            ⏳ No hay mensajes aún. Esperando eventos...
                        </div>
                    ) : (
                        messages.map((msg, index) => (
                            <div
                                key={index}
                                style={{
                                    marginBottom: "15px",
                                    padding: "12px",
                                    backgroundColor: "#1e1e1e",
                                    borderRadius: "3px",
                                    borderLeft: `4px solid ${
                                        msg.type === "success" ? "#00ff00" :
                                        msg.type === "error" ? "#ff0000" :
                                        "#0099ff"
                                    }`
                                }}
                            >
                                <div style={{ marginBottom: "8px", color: "#808080", fontSize: "12px" }}>
                                    🕐 [{msg.timestamp}]
                                </div>
                                {msg.type === "data" ? (
                                    <>
                                        <div style={{ marginBottom: "5px" }}>
                                            <span style={{ color: "#00ff00" }}>📨</span>
                                            {" "}<strong>Canal:</strong> {msg.channel}
                                        </div>
                                        <div style={{ marginBottom: "5px" }}>
                                            <strong>Evento:</strong> {msg.event}
                                        </div>
                                        <div style={{ marginTop: "10px" }}>
                                            <strong>Datos:</strong>
                                            <pre style={{
                                                marginTop: "5px",
                                                padding: "10px",
                                                backgroundColor: "#000",
                                                borderRadius: "3px",
                                                overflow: "auto",
                                                fontSize: "12px",
                                                border: "1px solid #333"
                                            }}>
                                                {JSON.stringify(msg.data, null, 2)}
                                            </pre>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        {msg.type === "success" ? "✅" : "❌"}
                                        <span>{msg.message}</span>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};
